import React, { useCallback, useRef, useState } from 'react';
import { Document, PDFViewer, Font } from '@react-pdf/renderer';
import fontRegular from 'src/assets/fonts/Poppins-Regular.ttf';
import fontMedium from 'src/assets/fonts/Poppins-Medium.ttf';
import fontBold from 'src/assets/fonts/Poppins-Bold.ttf';
import { ControlsPdfQuery } from 'src/graphql/_generated';
import _ from 'lodash';
import { HtmlWrap } from '../comps';
import { ActionsConf, Toc, TocItem } from '../types';
import { RmpHead, RmpToc, RmpSummary, RmpRender, RmpEnd } from './comps';
import { IS_DEBUG } from '../conf';
import { auditEvent } from 'src/api/other';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { ColorSet } from 'src/redux/reducers/theme';
import { useOrganisationBranding } from 'src/hooks/useOrganisationBranding';

Font.register({
  family: 'Poppins',

  fonts: [
    {
      src: fontRegular,
      fontWeight: 400,
    },
    {
      src: fontMedium,
      fontWeight: 500,
    },
    {
      src: fontBold,
      fontWeight: 600,
    },
  ],
});

type ControlPdf = ControlsPdfQuery['controls'][number];

interface PdfRiskManagementProps {
  data: ControlPdf[];
  onClose: () => void;
  colors: ColorSet;
  includesActions: ActionsConf;
}

const PdfRiskManagement: React.FC<PdfRiskManagementProps> = ({ data, onClose, colors, includesActions }) => {
  const {
    logoUrl: brandingLogo,
    primaryColor: brandingPrimaryColor,
    secondaryColor: brandingSecondaryColor,
  } = useOrganisationBranding();

  const [tableOfContents, setTableOfContents] = useState<Toc[]>([
    { title: 'Risk Management Plan', pageNumber: -1, id: 'rmp' },
    {
      title: 'Controls',
      pageNumber: -1,
      id: data[0].name.toLowerCase(),
      children: data.map((el) => ({
        title: el.name,
        pageNumber: 0,
      })),
    },
  ]);

  const tocDataRef = useRef<{
    plan?: { pageNumber: number };
    controls?: { data: TocItem[] };
  }>({});
  const tocFinalised = useRef(false);
  const tocRenderedOnce = useRef(false);
  const printedRef = useRef(false);

  const finaliseToc = useCallback(() => {
    if (tocFinalised.current) return;
    if (!tocDataRef.current.plan || !tocDataRef.current.controls) return;

    tocFinalised.current = true;
    setTableOfContents((prev) => {
      const contents = _.cloneDeep(prev);
      contents[0].pageNumber = tocDataRef.current.plan!.pageNumber;

      const controlsData = tocDataRef.current.controls!.data;
      if (controlsData.length) {
        contents[1] = {
          title: 'Controls',
          pageNumber: controlsData[0].pageNumber,
          id: controlsData[0].title,
          children: controlsData,
        };
      }

      return contents;
    });
  }, []);

  const onRmpSummaryDone = useCallback(
    (pageNumber: number) => {
      if (!tocDataRef.current.plan) {
        tocDataRef.current.plan = { pageNumber };
        finaliseToc();
      }
    },
    [finaliseToc],
  );

  const onControlsDone = useCallback(
    (data: TocItem[]) => {
      if (!tocDataRef.current.controls) {
        tocDataRef.current.controls = { data };
        finaliseToc();
      }
    },
    [finaliseToc],
  );

  // FILE DOWNLOAD
  const print = (blob: Blob | undefined) => {
    if (blob) {
      let elm = document.createElement('a');
      elm.href = URL.createObjectURL(blob);
      elm.setAttribute('download', 'Risk Management Plan.pdf');
      elm.click();
      elm.remove();

      auditEvent({
        action: 'ExportPdf',
        resource: 'control',
        control_id: data.map((el) => el.id),
      }).then(() => queryCache.invalidateQueries([QKeys.Audit]));

      onClose();
    }
  };

  if (!data) onClose();

  return (
    <HtmlWrap $isTest={IS_DEBUG}>
      <PDFViewer>
        <Document
          onRender={(el) => {
            if (!tocFinalised.current) return;

            if (tocRenderedOnce.current) {
              if (!IS_DEBUG && !printedRef.current) {
                printedRef.current = true;
                print(el.blob);
              }
            } else {
              tocRenderedOnce.current = true;
            }
          }}
        >
          <RmpHead
            numberOfStrategies={data.length}
            colors={colors}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
            secondaryColor={brandingSecondaryColor}
          />
          <RmpToc
            contents={tableOfContents}
            colors={colors}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
          />
          <RmpSummary
            onDone={onRmpSummaryDone}
            data={data}
            colors={colors}
            logoSrc={brandingLogo}
            secondaryColor={brandingSecondaryColor}
          />
          <RmpRender
            controls={data}
            onDone={onControlsDone}
            colors={colors}
            includesActions={includesActions}
            logoSrc={brandingLogo}
            secondaryColor={brandingSecondaryColor}
          />
          <RmpEnd
            companyImage={undefined}
            colors={colors}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
            secondaryColor={brandingSecondaryColor}
          />
        </Document>
      </PDFViewer>
    </HtmlWrap>
  );
};

export default PdfRiskManagement;
